import React, { useEffect, useRef, useState } from 'react';
import {
  Modal,
  Grid,
  Typography,
  Button,
  Alert,
  TextField,
} from '@mui/material';
import {
  CheckIcon,
  ContentCopyIcon,
} from '../../../../../assets/icons/icons';

/**
 * Generic "copy value" modal for reuse across forms.
 *
 * Props:
 * - open: boolean
 * - onClose: () => void
 * - value: string (what to copy)
 * - title: string
 * - label: string (caption above the value)
 * - emptyValueText: string shown when value is empty
 * - autoCloseMs: number | null
 * - copySuccessMessage: string used by onCopySuccess callback (optional)
 * - onCopySuccess: (message: string) => void (optional)
 * - onCopyError: (err: unknown) => void (optional)
 */
const CopyToClipboardModal = ({
  open,
  onClose,
  value,
  title = 'Copy value',
  label = 'Value',
  emptyValueText = 'N/A',
  autoCloseMs = null,
  copySuccessMessage,
  onCopySuccess,
  onCopyError,
}) => {
  const [hasCopied, setHasCopied] = useState(false);
  const autoCloseTimerRef = useRef(null);

  const normalizedValue =
    value != null && String(value).trim() !== '' ? String(value).trim() : '';

  useEffect(() => {
    // Reset "copied" state whenever the modal opens or the value changes.
    if (open) setHasCopied(false);
  }, [open, normalizedValue]);

  useEffect(() => {
    // If the modal is closed before auto-close, cancel the pending timer.
    if (!open && autoCloseTimerRef.current) {
      clearTimeout(autoCloseTimerRef.current);
      autoCloseTimerRef.current = null;
    }
  }, [open]);

  useEffect(() => {
    return () => {
      if (autoCloseTimerRef.current) {
        clearTimeout(autoCloseTimerRef.current);
        autoCloseTimerRef.current = null;
      }
    };
  }, []);

  const handleCopy = async () => {
    if (!normalizedValue) return;
    try {
      await navigator.clipboard.writeText(normalizedValue);
      setHasCopied(true);
      if (onCopySuccess) {
        const seconds = autoCloseMs ? Math.ceil(autoCloseMs / 1000) : null;
        const autoCloseSuffix =
          seconds && seconds > 0
            ? ` This modal will auto-close in ${seconds} seconds.`
            : '';

        if (copySuccessMessage) {
          const needsSuffix = !copySuccessMessage
            .toLowerCase()
            .includes('auto-close');
          onCopySuccess(
            needsSuffix
              ? `${copySuccessMessage}${autoCloseSuffix}`
              : copySuccessMessage
          );
        } else {
          const baseLabel = label || title || 'Value';
          onCopySuccess(`${baseLabel} copied!${autoCloseSuffix}`);
        }
      }

      if (autoCloseMs && typeof autoCloseMs === 'number' && autoCloseMs > 0) {
        if (autoCloseTimerRef.current) {
          clearTimeout(autoCloseTimerRef.current);
        }
        autoCloseTimerRef.current = setTimeout(() => {
          onClose();
        }, autoCloseMs);
      }
    } catch (error) {
      console.error('Error copying to clipboard:', error);
      if (onCopyError) onCopyError(error);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby='modal-modal-title'
      aria-describedby='modal-modal-description'
    >
      <Grid
        container
        spacing={2}
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '92vw', sm: '78vw', md: 560 },
          bgcolor: 'var(--dust-white)',
          p: 4,
          overflowY: 'auto',
          maxHeight: { xs: '90vh', md: '85vh' },
        }}
      >
        <Grid size={12}>
          <Typography
            id='modal-modal-title'
            sx={{ fontFamily: 'BrightstarSans-Regular', fontSize: '25px' }}
          >
            {title}
          </Typography>
        </Grid>

        <Grid size={12}>
          <TextField
            value={normalizedValue || emptyValueText}
            sx={{
              backgroundColor: '#ffffff',
              marginBottom: '15px',
              width: '100%',
            }}
            slotProps={{
              input: {
                readOnly: true,
                sx: {
                  fontFamily: 'ui-monospace, Menlo, Monaco, monospace',
                  fontWeight: 700,
                  color: '#000000',
                },
              },
            }}
          />

          {!normalizedValue && (
            <Alert severity='warning' variant='outlined'>
              The server did not return the value. The submission may have
              succeeded — check the report or try again.
            </Alert>
          )}
        </Grid>

        <Grid
          size={12}
          sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}
        >
          <Button
            disabled={!normalizedValue || hasCopied}
            variant='contained'
            sx={{ backgroundColor: 'var(--vega-blue)' }}
            onClick={handleCopy}
            startIcon={hasCopied ? <CheckIcon /> : <ContentCopyIcon />}
          >
            {hasCopied ? 'Copied' : 'Copy'}
          </Button>
          <Button
            variant='contained'
            onClick={onClose}
            sx={{ backgroundColor: 'var(--arcturus-red)' }}
          >
            Close
          </Button>
        </Grid>
      </Grid>
    </Modal>
  );
};

export default CopyToClipboardModal;

